// On-device debug console overlay for Capacitor Android.
// Captures console.log/warn/error and renders the last N entries on-screen.
// Tap to expand/collapse, long-press to clear.
//
// Activated only on native AND only when REACT_APP_DEBUG_OVERLAY=1
// (so it never ships to web users).

import { Capacitor } from '@capacitor/core';

const ENABLED =
  Capacitor.isNativePlatform?.() === true &&
  process.env.REACT_APP_DEBUG_OVERLAY === '1';

const MAX_LINES = 80;
const lines = [];

let host = null;
let log = null;
let expanded = true;

const colorFor = (lvl) => ({
  log: '#9ca3af',
  info: '#60a5fa',
  warn: '#fbbf24',
  error: '#f87171',
}[lvl] || '#fff');

const fmt = (args) =>
  args.map((a) => {
    if (a == null) return String(a);
    if (typeof a === 'string') return a;
    try { return JSON.stringify(a); } catch { return String(a); }
  }).join(' ');

const render = () => {
  if (!log) return;
  log.innerHTML = lines.slice(-MAX_LINES).map((e) =>
    `<div style="color:${colorFor(e.level)};white-space:pre-wrap;word-break:break-word;font-size:10px;line-height:1.3;margin-bottom:2px;">[${e.t}] ${e.msg.replace(/&/g,'&amp;').replace(/</g,'&lt;')}</div>`
  ).join('');
  log.scrollTop = log.scrollHeight;
};

const push = (level, args) => {
  const now = new Date();
  const t = `${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}.${String(now.getMilliseconds()).padStart(3,'0')}`;
  lines.push({ level, t, msg: fmt(args) });
  if (lines.length > MAX_LINES * 2) lines.splice(0, lines.length - MAX_LINES);
  render();
};

const wrapConsole = () => {
  ['log', 'info', 'warn', 'error'].forEach((lvl) => {
    const orig = console[lvl].bind(console);
    console[lvl] = (...args) => {
      try { push(lvl, args); } catch (_) {}
      orig(...args);
    };
  });
  window.addEventListener('error', (e) => {
    push('error', [`window.error: ${e.message} @ ${e.filename}:${e.lineno}`]);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    let detail;
    if (reason && typeof reason === 'object') {
      detail = `${reason.message || ''} (code=${reason.code || '?'}) stack=${(reason.stack || '').split('\n').slice(0, 3).join(' | ')}`;
    } else {
      detail = String(reason);
    }
    push('error', [`unhandledrejection: ${detail}`]);
  });
};

const mount = () => {
  if (host) return;
  host = document.createElement('div');
  host.id = 'linksy-debug-overlay';
  host.style.cssText = `
    position: fixed; left: 8px; right: 8px; bottom: 8px; z-index: 2147483647;
    background: rgba(0,0,0,0.85); color: #fff; border-radius: 8px;
    font-family: monospace; padding: 6px; max-height: 45vh; display: flex; flex-direction: column;
    border: 1px solid #444; box-shadow: 0 4px 16px rgba(0,0,0,0.5);
    pointer-events: auto;
  `;

  const header = document.createElement('div');
  header.style.cssText = `display:flex;align-items:center;gap:8px;font-size:10px;padding:2px 4px;`;
  header.innerHTML = `
    <span style="color:#10b981;">●</span>
    <span style="font-weight:600;">DEBUG</span>
    <span style="opacity:0.6;" id="dbg-count">0</span>
    <span style="flex:1;"></span>
    <button id="dbg-clear" style="background:#3f3f46;color:#fff;border:0;border-radius:4px;padding:2px 6px;font-size:10px;font-family:monospace;">clear</button>
    <button id="dbg-toggle" style="background:#3f3f46;color:#fff;border:0;border-radius:4px;padding:2px 6px;font-size:10px;font-family:monospace;">_</button>
    <button id="dbg-hide" style="background:#7f1d1d;color:#fff;border:0;border-radius:4px;padding:2px 6px;font-size:10px;font-family:monospace;">x</button>
  `;
  host.appendChild(header);

  log = document.createElement('div');
  log.id = 'dbg-log';
  log.style.cssText = `flex:1;overflow-y:auto;padding:4px;`;
  host.appendChild(log);

  document.body.appendChild(host);

  header.querySelector('#dbg-clear').addEventListener('click', () => {
    lines.length = 0; render();
  });
  header.querySelector('#dbg-toggle').addEventListener('click', () => {
    expanded = !expanded;
    log.style.display = expanded ? 'block' : 'none';
    host.style.maxHeight = expanded ? '45vh' : '32px';
  });
  header.querySelector('#dbg-hide').addEventListener('click', () => {
    host.remove(); host = null;
  });

  // Update count badge
  setInterval(() => {
    const c = document.getElementById('dbg-count');
    if (c) c.textContent = `${lines.length}`;
  }, 500);
};

export const initDebugOverlay = () => {
  if (!ENABLED) return;
  wrapConsole();
  if (document.body) mount();
  else document.addEventListener('DOMContentLoaded', mount);
  // Expose a global toggle so we can re-show after hiding
  window._showDebugOverlay = () => { if (!host) mount(); };
};

initDebugOverlay();
